import { create } from "zustand";
import { api, User, Organization, MspClientOrganization } from "@/lib/api";
import { useDataStore } from "./data";

// Demo mode check - same as in api.ts
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

interface AuthState {
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // MSP (Managed Service Provider) state
  isMsp: boolean;
  mspClients: MspClientOrganization[];
  activeClientOrg: MspClientOrganization | null;
  isMspLoading: boolean;

  // Partner tier helpers (derived from organization)
  partnerTier: "reseller" | "white_label" | null;
  isWhiteLabel: boolean;  // Shorthand for tier === 'white_label'

  // Actions
  loginWithCode: (code: string, isNewUser?: boolean) => Promise<boolean>;
  login: (token: string) => Promise<void>; // @deprecated - kept for migration
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshOrganization: () => Promise<void>;

  // MSP actions
  loadMspClients: () => Promise<void>;
  setActiveClient: (clientId: string | null) => void;
  clearMspContext: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organization: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  // MSP state
  isMsp: false,
  mspClients: [],
  activeClientOrg: null,
  isMspLoading: false,

  // Partner tier (derived)
  partnerTier: null,
  isWhiteLabel: false,

  /**
   * New secure login flow using exchange codes.
   * Exchanges the code for a session (HTTP-only cookie set by server).
   * Returns true on success, false on failure.
   */
  loginWithCode: async (code: string, _isNewUser?: boolean): Promise<boolean> => {
    try {
      set({ isLoading: true, error: null });

      // Exchange the code for a session (cookie set by server)
      await api.exchangeCode(code);

      // Now fetch user and org data using the new session
      const [userRes, orgRes] = await Promise.all([
        api.getMe(),
        api.getOrganization(),
      ]);

      const isMsp = orgRes.organization.organization_type === "msp";
      const partnerTier = orgRes.organization.partner_tier || null;
      set({
        user: userRes.user,
        organization: orgRes.organization,
        isAuthenticated: true,
        isLoading: false,
        isMsp,
        partnerTier,
        isWhiteLabel: partnerTier === "white_label",
      });

      // If MSP, load clients in background
      if (isMsp) {
        get().loadMspClients();
      }

      return true;
    } catch (error) {
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : "Login failed",
        partnerTier: null,
        isWhiteLabel: false,
      });
      return false;
    }
  },

  /**
   * @deprecated This method is kept for backward compatibility during migration.
   * Use loginWithCode for the new secure flow.
   */
  login: async (token: string) => {
    try {
      set({ isLoading: true, error: null });
      api.setToken(token);

      const [userRes, orgRes] = await Promise.all([
        api.getMe(),
        api.getOrganization(),
      ]);

      const partnerTier = orgRes.organization.partner_tier || null;
      set({
        user: userRes.user,
        organization: orgRes.organization,
        isAuthenticated: true,
        isLoading: false,
        partnerTier,
        isWhiteLabel: partnerTier === "white_label",
      });
    } catch (error) {
      api.setToken(null);
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : "Login failed",
        partnerTier: null,
        isWhiteLabel: false,
      });
    }
  },

  logout: async () => {
    try {
      // Notify server to invalidate session
      await api.logout();
    } catch (error) {
      // Continue with local logout even if server call fails
      console.error("Server logout failed:", error);
    } finally {
      api.setToken(null);
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  logoutAll: async () => {
    try {
      // Notify server to invalidate all sessions
      await api.logoutAll();
    } catch (error) {
      console.error("Server logout-all failed:", error);
    } finally {
      api.setToken(null);
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  checkAuth: async () => {
    // In demo mode, set a fake token and proceed with auth
    if (DEMO_MODE) {
      api.setToken("demo_token");
    }

    // With cookie-based auth, we check by making a request to /auth/me
    // The HTTP-only cookie will be sent automatically
    // We also check for legacy tokens during migration period
    const legacyToken = api.getToken();

    // If no legacy token and not in demo mode, still try the API
    // as we might have a valid HTTP-only cookie
    if (!legacyToken && !DEMO_MODE) {
      // Try to authenticate with cookie
      try {
        set({ isLoading: true });
        const [userRes, orgRes] = await Promise.all([
          api.getMe(),
          api.getOrganization(),
        ]);

        const isMsp = orgRes.organization.organization_type === "msp";
        const partnerTier = orgRes.organization.partner_tier || null;
        set({
          user: userRes.user,
          organization: orgRes.organization,
          isAuthenticated: true,
          isLoading: false,
          isMsp,
          partnerTier,
          isWhiteLabel: partnerTier === "white_label",
        });

        // If MSP, load clients in background
        if (isMsp) {
          get().loadMspClients();
        }
        return;
      } catch {
        // No valid session (cookie or legacy token)
        set({
          user: null,
          organization: null,
          isAuthenticated: false,
          isLoading: false,
          partnerTier: null,
          isWhiteLabel: false,
        });
        return;
      }
    }

    try {
      set({ isLoading: true });
      const [userRes, orgRes] = await Promise.all([
        api.getMe(),
        api.getOrganization(),
      ]);

      const isMsp = orgRes.organization.organization_type === "msp";
      const partnerTier = orgRes.organization.partner_tier || null;
      set({
        user: userRes.user,
        organization: orgRes.organization,
        isAuthenticated: true,
        isLoading: false,
        isMsp,
        partnerTier,
        isWhiteLabel: partnerTier === "white_label",
      });

      // If MSP, load clients in background
      if (isMsp) {
        get().loadMspClients();
      }
    } catch {
      // In demo mode, don't clear the token on error
      if (!DEMO_MODE) {
        api.setToken(null);
      }
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        partnerTier: null,
        isWhiteLabel: false,
      });
    }
  },

  refreshUser: async () => {
    if (!get().isAuthenticated) return;

    try {
      const { user } = await api.getMe();
      set({ user });
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  },

  refreshOrganization: async () => {
    if (!get().isAuthenticated) return;

    try {
      const { organization } = await api.getOrganization();
      // Check if this is an MSP organization
      const isMsp = organization.organization_type === "msp";
      const partnerTier = organization.partner_tier || null;
      set({
        organization,
        isMsp,
        partnerTier,
        isWhiteLabel: partnerTier === "white_label",
      });

      // If MSP, load clients
      if (isMsp) {
        get().loadMspClients();
      }
    } catch (error) {
      console.error("Failed to refresh organization:", error);
    }
  },

  // MSP Actions
  loadMspClients: async () => {
    if (!get().isAuthenticated || !get().isMsp) return;

    try {
      set({ isMspLoading: true });
      const response = await api.getMspClients();
      set({
        mspClients: response.clients || [],
        isMspLoading: false,
      });
    } catch (error) {
      console.error("Failed to load MSP clients:", error);
      set({ isMspLoading: false });
    }
  },

  setActiveClient: (clientId: string | null) => {
    const { mspClients } = get();

    // Invalidate all cached data when switching context
    // This ensures pages refetch fresh data for the new org
    useDataStore.getState().invalidateAll();

    if (clientId === null) {
      // Return to MSP aggregate view
      api.setClientOrg(null);
      set({ activeClientOrg: null });
      return;
    }

    const client = mspClients.find((c) => c.id === clientId);
    if (client) {
      // Set the client context on the API client
      // This makes all subsequent API calls return that client's data
      api.setClientOrg(client.id);
      set({ activeClientOrg: client });
    }
  },

  clearMspContext: () => {
    api.setClientOrg(null);
    set({
      isMsp: false,
      mspClients: [],
      activeClientOrg: null,
      isMspLoading: false,
    });
  },
}));

// Initialize auth check on app load
useAuthStore.getState().checkAuth();
